/**
 * Shared lasso point-in-polygon selection for scatter charts (SimpleScatter, ExploreScatterUPlot).
 */
import LassoSelectionWorker from '../workers/lasso-selection-processor.ts?worker';
import { error as logError } from './console';
import {
  setSelectedEvents,
  setSelectedRanges,
  setHasSelection,
  setTriggerSelection,
  selectedEvents,
  selectedRanges,
} from '../store/selectionStore';

/** Matches `Styles.css` — wait cursor while lasso polygon selection is computed in a worker */
export const LASSO_COMPUTING_CLASS = 'advanced-scatter-lasso-computing';

export function setLassoComputingCursor(on: boolean): void {
  if (typeof document === 'undefined') {return;}
  document.documentElement.classList.toggle(LASSO_COMPUTING_CLASS, on);
}

export interface LassoPointInput {
  id: string;
  x: number;
  y: number;
}

let sharedWorker: Worker | null = null;

function getWorker(): Worker {
  if (!sharedWorker) {
    sharedWorker = new LassoSelectionWorker();
  }
  return sharedWorker;
}

export async function computeLassoSelectedIds(
  points: LassoPointInput[],
  polygon: [number, number][]
): Promise<string[]> {
  if (points.length === 0 || polygon.length < 3) {return [];}

  const worker = getWorker();
  const messageId = `lasso-selection-${Date.now()}-${Math.random()}`;
  const timeoutMs = Math.min(
    120_000,
    Math.max(20_000, 12_000 + points.length * 8 + polygon.length * 40)
  );

  return new Promise<string[]>((resolve, reject) => {
    const timeout = setTimeout(() => {
      worker.removeEventListener('message', handleMessage);
      reject(new Error(`Lasso selection timeout after ${timeoutMs}ms`));
    }, timeoutMs);

    const handleMessage = (event: MessageEvent) => {
      if (event.data.id !== messageId) {return;}
      clearTimeout(timeout);
      worker.removeEventListener('message', handleMessage);
      if (event.data.type === 'success' && event.data.result) {
        resolve(event.data.result.selectedIds as string[]);
      } else {
        reject(new Error(event.data.error || 'Unknown error'));
      }
    };

    worker.addEventListener('message', handleMessage);
    worker.postMessage({
      id: messageId,
      type: 'COMPUTE_LASSO_SELECTION',
      data: { points, polygon },
      timestamp: Date.now(),
    });
  });
}

/** Resolve event_id from a scatter row (aggregates / maneuvers). */
export function resolveScatterPointEventId(row: Record<string, unknown>): number | null {
  const raw = row.event_id ?? row.event_ID ?? row.EVENT_ID ?? row.Event_id;
  if (raw == null) {return null;}
  const n = Number(raw);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
}

/** Resolve ISO datetime string from a scatter row (server_file mode). */
export function resolveScatterPointDatetimeIso(row: Record<string, unknown>): string | null {
  const raw = row.Datetime ?? row.datetime ?? row.timestamp;
  if (raw == null) {return null;}
  try {
    const date = raw instanceof Date ? raw : new Date(String(raw));
    if (isNaN(date.getTime())) {return null;}
    return date.toISOString();
  } catch {
    return null;
  }
}

export type LassoSelectionMode = 'event_id' | 'datetime';

/** Prefer event_id when any point in the dataset has one; else datetime (file mode). */
export function detectLassoSelectionMode(rows: Record<string, unknown>[]): LassoSelectionMode {
  for (const row of rows) {
    if (resolveScatterPointEventId(row) != null) {return 'event_id';}
  }
  return 'datetime';
}

/**
 * Commit lasso results to selectionStore.
 * Event mode → setSelectedEvents; datetime mode → setSelectedRanges (one range per point).
 */
export function commitLassoSelection(
  selectedIds: string[],
  mode: LassoSelectionMode,
  rowsById: Map<string, Record<string, unknown>>
): void {
  if (selectedIds.length === 0) {return;}

  if (mode === 'event_id') {
    const numericIds = selectedIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && !Number.isNaN(id));
    if (numericIds.length === 0) {return;}

    const current = selectedEvents();
    const merged = new Set([...current, ...numericIds]);
    setSelectedEvents(Array.from(merged));
    setHasSelection(merged.size > 0);
    setTriggerSelection(true);
    return;
  }

  const ranges: Array<{ start_time: string; end_time: string }> = [];
  for (const id of selectedIds) {
    const row = rowsById.get(id);
    if (!row) {continue;}
    const iso = resolveScatterPointDatetimeIso(row);
    if (!iso) {continue;}
    ranges.push({ start_time: iso, end_time: iso });
  }
  if (ranges.length === 0) {return;}

  const existing = selectedRanges();
  const merged = [...existing, ...ranges];
  setSelectedRanges(merged);
  setHasSelection(true);
  setTriggerSelection(true);
}

/** Build screen-space point list from D3 circle nodes (AdvancedScatter pattern). */
export function buildScreenSpaceLassoPoints(
  data: Record<string, unknown>[],
  nodes: Array<Element | null>,
  getPointId: (row: Record<string, unknown>) => string | null
): { points: LassoPointInput[]; rowsById: Map<string, Record<string, unknown>> } {
  const points: LassoPointInput[] = [];
  const rowsById = new Map<string, Record<string, unknown>>();
  const len = Math.min(data.length, nodes.length);

  for (let i = 0; i < len; i++) {
    const d = data[i];
    if (!d) {continue;}
    const id = getPointId(d);
    if (!id) {continue;}
    const node = nodes[i];
    if (!node) {continue;}
    const box = node.getBoundingClientRect();
    points.push({
      id,
      x: box.left + box.width / 2,
      y: box.top + box.height / 2,
    });
    rowsById.set(id, d);
  }

  return { points, rowsById };
}

/** Build data-space point list converted to screen coords via scales (uPlot overlay). */
export function buildPlotSpaceLassoPoints(
  data: Record<string, unknown>[],
  toScreen: (x: number, y: number) => { x: number; y: number } | null,
  getPointId: (row: Record<string, unknown>) => string | null
): { points: LassoPointInput[]; rowsById: Map<string, Record<string, unknown>> } {
  const points: LassoPointInput[] = [];
  const rowsById = new Map<string, Record<string, unknown>>();

  for (const d of data) {
    const id = getPointId(d);
    if (!id) {continue;}
    const x = Number(d.x);
    const y = Number(d.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {continue;}
    const screen = toScreen(x, y);
    if (!screen) {continue;}
    points.push({ id, x: screen.x, y: screen.y });
    rowsById.set(id, d);
  }

  return { points, rowsById };
}

export { buildUPlotOverlayLassoPoints } from "./uPlotScatterLassoCoords";

export function isPointInActiveLassoSelection(
  d: Record<string, unknown>,
  activeEvents: number[],
  activeRanges: Array<{ start_time?: string; end_time?: string }>,
  cutActive: boolean
): boolean {
  if (cutActive) {return true;}
  if (activeEvents.length > 0) {
    const eid = resolveScatterPointEventId(d);
    return eid != null && activeEvents.includes(eid);
  }
  if (activeRanges.length > 0) {
    const rawTs = d.Datetime ?? d.datetime ?? d.timestamp;
    if (rawTs == null) {return false;}
    const ts =
      rawTs instanceof Date ? rawTs.getTime() : new Date(String(rawTs)).getTime();
    if (!Number.isFinite(ts)) {return false;}
    return activeRanges.some((range) => {
      if (!range.start_time || !range.end_time) {return false;}
      return (
        ts >= new Date(range.start_time).getTime() &&
        ts <= new Date(range.end_time).getTime()
      );
    });
  }
  return true;
}

export async function runLassoSelectionCommit(
  points: LassoPointInput[],
  rowsById: Map<string, Record<string, unknown>>,
  polygon: [number, number][],
  mode: LassoSelectionMode
): Promise<void> {
  setLassoComputingCursor(true);
  try {
    const selectedIds = await computeLassoSelectedIds(points, polygon);
    commitLassoSelection(selectedIds, mode, rowsById);
  } catch (err: unknown) {
    logError('scatterLassoSelection: computation failed', err);
  } finally {
    setLassoComputingCursor(false);
  }
}
